import Badge from "@/components/common/Badge";
import { API_BASE_URL } from "@/lib/axios";
import { motion } from "framer-motion";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

interface CourseCardProps {
  _id: string;
  title: string;
  courseDescription: string;
  courseLevel: "Beginner" | "Intermediate" | "Advanced";
  thumbnail: string;
  price: number;
  slug: string;
  createdAt: string;
  isActive: boolean;
  userProgress?: {
    progress: number;
    isCompleted: boolean;
    isInProgress: boolean;
    startedAt?: string;
    completedAt?: string;
    quizScores: any[];
    averageQuizScore: number;
  } | null;
}

const CourseCard = ({
  _id,
  title,
  courseDescription,
  courseLevel,
  thumbnail,
  userProgress,
}: CourseCardProps) => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = useState(false);

  const difficultyVariant = {
    Beginner: "success",
    Intermediate: "warning",
    Advanced: "error",
  }[courseLevel] as "success" | "warning" | "error";

  const imageUrl = thumbnail
    ? `${thumbnail}`
    : undefined;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      whileHover={{
        y: -5,
        transition: { duration: 0.2 },
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-shadow dark:border-gray-700 dark:bg-gray-800 hover:shadow-md dark:hover:shadow-gray-700/20"
    >
      <Link to={`/courses/${_id}`}>
        <div className="relative h-32 sm:h-40 overflow-hidden">
          {imageUrl && (
            <img
              src={imageUrl}
              alt={title || "Untitled"}
              className="h-full w-full object-cover transition-transform duration-500 ease-in-out"
              style={{
                transform: isHovered ? "scale(1.05)" : "scale(1)",
                maxWidth: '100%',
              }}
            />
          )}
        </div>
        <div className="p-3 sm:p-4">
          {/* Progress Status Indicators */}
          {userProgress && (
            <div className="mb-2 sm:mb-3 space-y-1.5 sm:space-y-2">
              {/* Progress Badge and Percentage */}
              <div className="flex items-center justify-between gap-2">
                {/* Progress Badge */}
                <div className="flex items-center space-x-1 sm:space-x-2 min-w-0">
                  {userProgress.isCompleted ? (
                    <Badge variant="success" className="text-[10px] sm:text-xs truncate">
                      <span className="mr-0.5 sm:mr-1">✅</span>
                      {t("courses.completed")}
                    </Badge>
                  ) : userProgress.isInProgress ? (
                    <Badge variant="warning" className="text-[10px] sm:text-xs truncate">
                      <span className="mr-0.5 sm:mr-1">🔄</span>
                      {t("courses.inProgress")}
                    </Badge>
                  ) : (
                    <Badge variant="primary" className="text-[10px] sm:text-xs truncate">
                      <span className="mr-0.5 sm:mr-1">📚</span>
                      {t("courses.notStarted")}
                    </Badge>
                  )}
                </div>
                
                {/* Progress Percentage */}
                {userProgress.isInProgress && (
                  <span className="text-[10px] sm:text-xs font-medium text-primary-600 dark:text-primary-400 whitespace-nowrap">
                    {userProgress.progress}%
                  </span>
                )}
              </div>
              
              {/* Progress Bar for In-Progress Courses */}
              {userProgress.isInProgress && (
                <div className="w-full bg-gray-200 rounded-full h-2 dark:bg-gray-700">
                  <div 
                    className="bg-gradient-to-r from-primary-500 to-primary-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${userProgress.progress}%` }}
                  ></div>
                </div>
              )}
              
              {/* Quiz Score for Completed Courses */}
              {userProgress.isCompleted && userProgress.averageQuizScore > 0 && (
                <div className="flex items-center justify-between text-xs">
                  <span className="text-gray-600 dark:text-gray-400">
                    {t("courses.averageQuizScore")}:
                  </span>
                  <span className="font-medium text-green-600 dark:text-green-400">
                    {userProgress.averageQuizScore}/5
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="mb-1.5 sm:mb-2 flex items-center justify-between gap-2">
            <Badge variant="primary" className="text-[10px] sm:text-xs truncate">{t("courses.category.other")}</Badge>
            <Badge variant={difficultyVariant} className="text-[10px] sm:text-xs truncate">
              {t(
                `courses.difficulty.${
                  courseLevel ? courseLevel.toLowerCase() : "beginner"
                }`
              )}
            </Badge>
          </div>
          
          <h3 className="mb-1 sm:mb-1.5 text-base sm:text-lg font-semibold text-gray-900 dark:text-white line-clamp-2">
            {title || "Untitled"}
          </h3>
          <p className="mb-2 sm:mb-3 text-xs sm:text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
            {courseDescription || ""}
          </p>
          <div className="flex items-center justify-between">
            <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 truncate">
              {t("courses.level")}: {courseLevel}
            </span>
            {/* {price !== undefined && (
              <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
                ${price}
              </span>
            )} */}
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

export default CourseCard;
